import { Link } from "react-router-dom";

const PrivacyPolicy = () => {
  return (
    <div className="w-full" data-testid="privacy-policy-page">
      <section className="bg-gradient-to-r from-blue-600 via-purple-600 to-orange-500 text-white py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4" data-testid="privacy-hero-title">Privacy Policy</h1>
          <p className="text-lg text-white/90">How we collect, use, and protect your personal data</p>
        </div>
      </section>

      <section className="py-12 md:py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 prose prose-lg max-w-none">
          <p className="text-gray-600 mb-8 text-sm">Last updated: January 2026</p>

          <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Introduction</h2>
          <p className="text-gray-700 mb-6 leading-relaxed">
            Maruns Limited ("we", "our", "us"), Company Number 16749241, is committed to protecting and respecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our logistics and transport services.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Information We Collect</h2>
          <p className="text-gray-700 mb-3 leading-relaxed">We may collect the following types of personal data:</p>
          <ul className="list-disc pl-6 text-gray-700 mb-6 space-y-2">
            <li><strong>Contact Information:</strong> Name, email address, telephone number, postal address</li>
            <li><strong>Service Details:</strong> Moving locations, dates, service requirements, and special instructions</li>
            <li><strong>Communications:</strong> Records of correspondence via email, phone, WhatsApp, or our contact form</li>
            <li><strong>Technical Data:</strong> IP address, browser type, operating system, and browsing behaviour on our website</li>
            <li><strong>Employment Data:</strong> CV, qualifications, and right-to-work documentation (for job applicants)</li>
          </ul>

          <h2 className="text-2xl font-bold text-gray-900 mb-4">3. How We Use Your Information</h2>
          <p className="text-gray-700 mb-3 leading-relaxed">We use the information we collect to:</p>
          <ul className="list-disc pl-6 text-gray-700 mb-6 space-y-2">
            <li>Provide and manage our logistics and transport services</li>
            <li>Respond to enquiries and provide quotations</li>
            <li>Process bookings and manage service delivery</li>
            <li>Communicate with you about your service or account</li>
            <li>Comply with legal and regulatory obligations</li>
            <li>Improve our website and services</li>
            <li>Process job applications</li>
          </ul>

          <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Legal Basis for Processing</h2>
          <p className="text-gray-700 mb-6 leading-relaxed">
            We process your personal data under the UK General Data Protection Regulation (UK GDPR) on the following lawful bases: performance of a contract, legitimate interests, consent, and compliance with legal obligations.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Data Sharing</h2>
          <p className="text-gray-700 mb-3 leading-relaxed">We may share your personal data with:</p>
          <ul className="list-disc pl-6 text-gray-700 mb-6 space-y-2">
            <li>Our employees and contractors who need access to fulfil your service</li>
            <li>Licensed partner companies (e.g., for regulated disposal or specialist services)</li>
            <li>Regulatory authorities, as required by law</li>
            <li>Professional advisors (legal, accounting, insurance)</li>
          </ul>
          <p className="text-gray-700 mb-6 leading-relaxed">
            We do not sell your personal data to third parties.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Data Retention</h2>
          <p className="text-gray-700 mb-6 leading-relaxed">
            We retain personal data only for as long as necessary to fulfil the purposes for which it was collected, or as required by law. Service records are typically retained for six years in accordance with UK business record-keeping requirements.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Your Rights</h2>
          <p className="text-gray-700 mb-3 leading-relaxed">Under UK GDPR, you have the right to:</p>
          <ul className="list-disc pl-6 text-gray-700 mb-6 space-y-2">
            <li>Access the personal data we hold about you</li>
            <li>Request correction of inaccurate data</li>
            <li>Request deletion of your data (where applicable)</li>
            <li>Object to or restrict certain processing</li>
            <li>Data portability</li>
            <li>Withdraw consent at any time (where processing is based on consent)</li>
          </ul>
          <p className="text-gray-700 mb-6 leading-relaxed">
            To exercise any of these rights, please contact us at <a href="mailto:marunsltd@outlook.com" className="text-blue-600 hover:underline">marunsltd@outlook.com</a>.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Cookies</h2>
          <p className="text-gray-700 mb-6 leading-relaxed">
            Our website may use essential cookies to ensure proper functionality. We do not use tracking or advertising cookies. By continuing to use our website, you consent to the use of essential cookies.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Data Security</h2>
          <p className="text-gray-700 mb-6 leading-relaxed">
            We implement appropriate technical and organisational measures to protect your personal data against unauthorised access, alteration, disclosure, or destruction. However, no method of transmission over the internet is 100% secure.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Changes to This Policy</h2>
          <p className="text-gray-700 mb-6 leading-relaxed">
            We may update this Privacy Policy from time to time. Any changes will be posted on this page with an updated revision date.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Contact Us</h2>
          <p className="text-gray-700 mb-2 leading-relaxed">If you have questions about this Privacy Policy, please contact us:</p>
          <div className="bg-gray-50 p-6 rounded-lg border border-gray-200 mb-8">
            <p className="text-gray-700"><strong>Maruns Limited</strong></p>
            <p className="text-gray-700">Innovation Centre and Business Base, 110 Butterfield, Great Marlings</p>
            <p className="text-gray-700">Luton, LU2 8DL, United Kingdom</p>
            <p className="text-gray-700">Email: <a href="mailto:marunsltd@outlook.com" className="text-blue-600 hover:underline">marunsltd@outlook.com</a></p>
            <p className="text-gray-700">Phone: <a href="tel:+447405747177" className="text-blue-600 hover:underline">+44 7405 747177</a></p>
          </div>

          <p className="text-gray-700 mb-6 leading-relaxed">
            You also have the right to lodge a complaint with the Information Commissioner's Office (ICO) at <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">ico.org.uk</a>.
          </p>

          <div className="text-center mt-12">
            <Link to="/" className="text-blue-600 hover:underline font-semibold" data-testid="privacy-back-home">Back to Home</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicy;
